// The reader: source → the authored tree of §2 and the diagnostics of §18.
//
// One pass over the lines, with a stack of open directives (§4.2's precedence):
//   1. whole-document metadata — front matter on the first line, backmatter after a plain `---`
//      separator (§3.1, §3.2);
//   2. fenced code, the literal HTML blocks and `$$` display math suspend recognition, and
//      comments are opaque until closed, their bytes leaving the body (§3.3, reference: Math);
//   3. outside those, container and leaf directives at physical column zero (§5.1), each
//      Markdown body read with the same rules, a structured one kept as data (§7);
//   4. within Markdown, alerts, keyword rules and their reasons at the start of a line, of a
//      list item or of a quoted line (§6.1) — a keyword line interrupts the prose before it,
//      as the draft's own compiled samples read it.
// Inline syntax (typed references, wiki-links, footnote references, math, `${…}`) stays in the
// Markdown nodes' source; `inline.ts` reads it.
import { attributesEnd, parseAttributes, type Attrs } from "./attributes";
import { BACKMATTER_KEYS, KEYWORD_FLAGS, KEYWORDS, KINDS, PATTERNS, RULES, attrSpec, type BodyFormat, type Form } from "./registry";
import type { AuthoredDocument, Diagnostic, DirectiveNode, Identity, MarkdownNode, Node, Parameter, Range, Severity, StructuredValue } from "./types";
import { loadCsv, loadJson, loadYaml } from "./yaml";

export interface ReadOptions {
  /** A part of a document (an extension's fallback, a selected fragment): no metadata regions. */
  fragment?: boolean;
  uri?: string;
}

export interface ReadResult {
  document: AuthoredDocument;
  diagnostics: Diagnostic[];
  /** Explicit identities (§5.3): directive and keyword IDs, heading `{#id}`s. */
  identities: Map<string, Identity>;
  /** Declared parameters (§12), from front matter and `param` leaves. */
  parameters: Map<string, Parameter>;
  /** Footnote labels defined in the document → the line of their definition. */
  footnotes: Map<string, number>;
  /** The Markdown nodes that are parsed prose — not an example's or a verbatim body's quoted text. */
  prose: Set<MarkdownNode>;
  /** The keyword a keyword rule or an alert was written with (`ALWAYS`, `AVOID`, `MUST NOT`); the tree keeps its normalized kind. */
  keywords: Map<DirectiveNode, string>;
  /** The lines of each rule's `BECAUSE:` reason, which the tree keeps as the rule's `because`. */
  reasons: Map<DirectiveNode, Range>;
  /** The lines that held nothing but a comment (§3.3), which leave the body. */
  comments: Set<number>;
  /** The source's lines (a leading BOM removed). */
  lines: string[];
  frontRange: Range | null;
  backRange: Range | null;
}

interface Line { text: string; n: number }

type Body = "parsed" | "literal" | "structured" | "opaque";

interface Fence { char: string; len: number }

type Unit =
  | { type: "rule"; node: DirectiveNode; lines: Line[]; shape: Shape; offset: number; blanks: Line[] }
  | { type: "reason"; rule: DirectiveNode | null; lines: Line[]; original: Line[]; shape: Shape; offset: number; blanks: Line[] }
  /** `last`: the alert's last line, which a lazy continuation line may follow only when it had text. */
  | { type: "alert"; node: DirectiveNode; lines: Line[]; last: string };

type Shape = "line" | "item" | "quote";

interface Frame {
  node: DirectiveNode | null;
  nodes: Node[];
  /** The opener's colon count; the scope itself has none. */
  fence: number;
  body: Body;
  format: BodyFormat;
  raw: Line[];
  rawCode: Fence | null;
  md: Line[];
  code: Fence | null;
  html: RegExp | "blank" | null;
  mathEnd: number;
  unit: Unit | null;
  lastRule: DirectiveNode | null;
  /** The previous line was blank (or this is the first line). */
  prevBlank: boolean;
  /** The previous line was paragraph text, which some blocks cannot interrupt. */
  paragraph: boolean;
  prevQuote: boolean;
  inList: boolean;
}

interface ScopeOptions { directives: boolean; keywords: boolean; document: boolean }

// ---- line grammar ----
const OPEN = /^(:{2,})([a-z][a-z0-9-]*(?::[a-z][a-z0-9-]*)?)(.*)$/;
const CLOSE = /^(:{3,})[ \t]*$/;
const FENCE = /^((?:[ \t]*>)*[ \t]*)(`{3,}|~{3,})(.*)$/;
const MATH = /^\$\$[ \t]*$/;
const KEYWORD_NAMES = Object.keys(KEYWORDS).sort((a, b) => b.length - a.length).join("|");
// KEYWORD[id] (if condition): text — with matched `**` when emphasized (§6.1)
const KEYWORD = new RegExp(`^(\\*\\*)?(${KEYWORD_NAMES})(?:\\[([^\\]\\s]*)\\])?(?: \\(if ([^()\\r\\n]*)\\))?:(\\*\\*)?[ \\t]+(\\S.*)$`);
const BECAUSE = /^(\*\*)?BECAUSE:(\*\*)?[ \t]+(\S.*)$/;
const ALERT = /^ {0,3}>[ \t]?\[!([A-Za-z]+(?:[ \t]+[A-Za-z]+)?)\][ \t]*$/;
const QUOTE = /^ {0,3}>[ \t]?(.*)$/;
const ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])([ \t]+)(.*)$/;
const ITEM_START = /^ {0,3}(?:[-*+]|\d{1,9}[.)])(?:[ \t]|$)/;
const HEADING = /^ {0,3}#{1,6}(?:[ \t]|$)/;
const THEMATIC = /^ {0,3}(?:(?:-[ \t]*){3,}|(?:\*[ \t]*){3,}|(?:_[ \t]*){3,})$/;
const HEADING_ATTRS = /^ {0,3}#{1,6}[ \t].*?[ \t](\{[^{}]*\})[ \t]*$/;
const FOOTNOTE_DEF = /^ {0,3}\[\^([^\]\s]+)\]:(?:[ \t]|$)/;
const HTML_LITERAL: [RegExp, RegExp][] = [
  [/^ {0,3}<(?:script|pre|style|textarea)(?=[\s>]|$)/i, /<\/(?:script|pre|style|textarea)>/i],
  [/^ {0,3}<\?/, /\?>/],
  [/^ {0,3}<!\[CDATA\[/, /\]\]>/],
  [/^ {0,3}<![A-Za-z]/, />/],
];
const HTML_BLOCK = /^ {0,3}<\/?(?:address|article|aside|base|basefont|blockquote|body|caption|center|col|colgroup|dd|details|dialog|dir|div|dl|dt|fieldset|figcaption|figure|footer|form|frame|frameset|h[1-6]|head|header|hr|html|iframe|legend|li|link|main|menu|menuitem|nav|noframes|ol|optgroup|option|p|param|search|section|summary|table|tbody|td|tfoot|th|thead|title|tr|track|ul)(?=[\s/>]|$)/i;
const HTML_TAG_LINE = /^ {0,3}(?:<[A-Za-z][A-Za-z0-9-]*(?:\s+[A-Za-z_:][\w.:-]*(?:\s*=\s*(?:[^\s"'=<>`]+|'[^']*'|"[^"]*"))?)*\s*\/?>|<\/[A-Za-z][A-Za-z0-9-]*\s*>)\s*$/;

/** The line patterns that decide what a line opens: for a layer that cannot load this reader (the studio's name
 *  scanner) to copy, and hold its copies to. */
export const LINE_PATTERNS = { open: OPEN, close: CLOSE, keyword: KEYWORD, because: BECAUSE } as const;

const indentOf = (s: string) => /^[ \t]*/.exec(s)![0].replace(/\t/g, "    ").length;
const blank = (s: string) => s.trim() === "";

export function read(text: string, opts: ReadOptions = {}): ReadResult {
  return new Reader(text, opts).run();
}

class Reader {
  readonly diagnostics: Diagnostic[] = [];
  readonly identities = new Map<string, Identity>();
  readonly parameters = new Map<string, Parameter>();
  readonly footnotes = new Map<string, number>();
  readonly prose = new Set<MarkdownNode>();
  readonly keywords = new Map<DirectiveNode, string>();
  readonly reasons = new Map<DirectiveNode, Range>();
  readonly comments = new Set<number>();
  readonly lines: string[];
  front: Record<string, unknown> = {};
  back: Record<string, unknown> = {};
  frontRange: Range | null = null;
  backRange: Range | null = null;
  private aliases = new Set<string>();

  constructor(source: string, private opts: ReadOptions) {
    const body = source.replace(/^\uFEFF/, "");
    const lines = body.split(/\r\n|\r|\n/);
    if (lines.length && lines[lines.length - 1] === "" && /(?:\r\n|\r|\n)$/.test(body)) lines.pop();
    if (body === "") lines.length = 0;
    this.lines = lines;
  }

  diag(code: string, severity: Severity, message: string, start?: number, end?: number) {
    this.diagnostics.push({ code, severity, message, ...(start ? { range: { start, end: end ?? start } } : {}) });
  }

  run(): ReadResult {
    let first = 0;
    if (!this.opts.fragment && this.lines[0] === "---") {
      const close = this.lines.indexOf("---", 1);
      if (close < 0) this.diag("invalid-metadata", "error", "the front matter is not closed: it ends with a line of exactly ---", 1);
      else {
        this.frontRange = { start: 1, end: close + 1 };
        this.front = this.metadata(this.lines.slice(1, close).join("\n"), 2, "front matter");
        first = close + 1;
      }
    }
    this.checkFront();
    const lines = this.lines.slice(first).map((text, i) => ({ text, n: first + i + 1 }));
    const body = this.scope(lines, { directives: true, keywords: true, document: !this.opts.fragment });
    this.checkParameters();
    const document: AuthoredDocument = {
      format: "instruction/1",
      ...(this.opts.uri ? { uri: this.opts.uri } : {}),
      metadata: { front: this.front, back: this.back },
      body,
    };
    this.diagnostics.sort((a, b) => (a.range?.start ?? 0) - (b.range?.start ?? 0) || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
    return { document, diagnostics: this.diagnostics, identities: this.identities, parameters: this.parameters, footnotes: this.footnotes, prose: this.prose, keywords: this.keywords, reasons: this.reasons, comments: this.comments, lines: this.lines, frontRange: this.frontRange, backRange: this.backRange };
  }

  /** A metadata region: a YAML mapping (§3.1, §3.2), or {} with an invalid-metadata error. */
  private metadata(yaml: string, line: number, what: string): Record<string, unknown> {
    const r = loadYaml(yaml);
    if (!r.ok) { this.diag("invalid-metadata", "error", `the ${what} is not valid YAML: ${r.message}`, line); return {}; }
    if (r.value === null || typeof r.value !== "object" || Array.isArray(r.value)) {
      this.diag("invalid-metadata", "error", `the ${what} must be a YAML mapping`, line);
      return {};
    }
    return r.value as Record<string, unknown>;
  }

  // ---- the scope: a document body, a container's body, a list item's ----

  private scope(lines: Line[], o: ScopeOptions): Node[] {
    const root = this.frame(null, Infinity, "parsed", "markdown");
    const stack: Frame[] = [root];
    let comment: { open: boolean; line: number } = { open: false, line: 0 };
    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      const top = stack[stack.length - 1];
      if (top.body !== "parsed") { this.rawLine(stack, line); continue; }
      if (top.code) { this.literal(top, line); if (closesFence(line.text, top.code)) top.code = null; continue; }
      if (top.html instanceof RegExp) { this.literal(top, line); if (top.html.test(line.text)) top.html = null; continue; }
      if (top.mathEnd) { this.literal(top, line); if (line.n === top.mathEnd) top.mathEnd = 0; continue; }

      // comments are opaque until closed; their bytes leave the body, and a line that was only a comment goes
      let text = line.text;
      if (comment.open || text.includes("<!--")) {
        const r = stripComments(text, comment.open);
        if (r.opened && !comment.open) comment.line = line.n;
        comment.open = r.open;
        if (r.text === null) { this.comments.add(line.n); continue; }
        text = r.text;
      }
      const cur: Line = text === line.text ? line : { text, n: line.n };

      if (top.html === "blank") {
        if (blank(cur.text)) top.html = null; else { this.literal(top, cur); continue; }
      }
      if (top.unit) {
        if (this.continueUnit(top, cur)) continue;
        this.endUnit(top);
      }
      if (o.document && stack.length === 1 && cur.text === "---" && top.prevBlank && li > 0 && backmatterFollows(lines, li)) {
        this.backmatter(lines, li);
        break;
      }
      const fence = FENCE.exec(cur.text);
      if (fence && !(fence[2][0] === "`" && fence[3].includes("`"))) {
        this.literal(top, cur);
        top.code = { char: fence[2][0], len: fence[2].length };
        continue;
      }
      const literalHtml = HTML_LITERAL.find(([open]) => open.test(cur.text));
      if (literalHtml) {
        this.literal(top, cur);
        if (!literalHtml[1].test(cur.text.replace(literalHtml[0], ""))) top.html = literalHtml[1];
        continue;
      }
      if (HTML_BLOCK.test(cur.text) || (!top.paragraph && HTML_TAG_LINE.test(cur.text))) {
        this.literal(top, cur);
        top.html = "blank";
        continue;
      }
      if (MATH.test(cur.text)) {
        const end = mathEnd(lines, li);
        if (end) { this.literal(top, cur); top.mathEnd = end; continue; }
        this.diag("math-unclosed", "warning", "a $$ display formula has no closing $$ line before the next directive; it stays as text", cur.n);
      }
      if (o.directives) {
        const close = CLOSE.exec(cur.text);
        if (close && stack.length > 1) { this.close(stack, close[1].length, cur.n); continue; }
        const open = OPEN.exec(cur.text);
        if (open && this.open(stack, open, cur)) continue;
      }
      if (o.keywords && this.startUnit(top, cur)) continue;
      this.proseLine(top, cur);
    }
    if (comment.open) this.diag("comment-unclosed", "warning", "a comment is not closed; it runs to the end of the file", comment.line);
    while (stack.length > 1) {
      const f = stack[stack.length - 1];
      this.diag("unclosed-directive", "error", `:::${f.node!.kind} is not closed: end it with a line of ${":".repeat(f.fence)}`, f.node!.range!.start);
      this.close(stack, f.fence, lines.length ? lines[lines.length - 1].n : f.node!.range!.start, true);
    }
    this.endFrame(root);
    return root.nodes;
  }

  private frame(node: DirectiveNode | null, fence: number, body: Body, format: BodyFormat): Frame {
    return { node, nodes: [], fence, body, format, raw: [], rawCode: null, md: [], code: null, html: null, mathEnd: 0, unit: null, lastRule: null, prevBlank: true, paragraph: false, prevQuote: false, inList: false };
  }

  // ---- prose ----

  /** A line of prose: read for heading identities and footnote definitions on the way in. */
  private proseLine(f: Frame, line: Line) {
    const t = line.text;
    if (!blank(t)) {
      f.lastRule = null;
      const h = HEADING_ATTRS.exec(t);
      if (h) this.headingIdentity(h[1], line.n);
      const fn = FOOTNOTE_DEF.exec(t);
      if (fn) {
        if (!PATTERNS.id.test(fn[1])) this.diag("invalid-attributes", "error", `the footnote label "${fn[1]}" is not valid: use letters, digits, ".", "_" and "-"`, line.n);
        else if (this.footnotes.has(fn[1])) this.diag("footnote-duplicate", "error", `the footnote [^${fn[1]}] is defined twice (first on line ${this.footnotes.get(fn[1])})`, line.n);
        else this.footnotes.set(fn[1], line.n);
      }
    }
    f.inList = ITEM_START.test(t) ? !THEMATIC.test(t) : f.inList && (blank(t) || indentOf(t) >= 2);
    f.prevQuote = QUOTE.test(t) || (f.prevQuote && !blank(t) && !startsBlock(t));
    f.md.push(line);
    f.prevBlank = blank(t);
    f.paragraph = !blank(t) && !HEADING.test(t) && !THEMATIC.test(t);
  }

  /** A line inside code, a literal HTML block or display math. */
  private literal(f: Frame, line: Line) {
    f.md.push(line);
    f.lastRule = null;
    f.prevBlank = f.paragraph = f.prevQuote = false;
  }

  private flush(f: Frame) {
    if (f.md.some((l) => !blank(l.text))) {
      const node: MarkdownNode = { type: "markdown", source: f.md.map((l) => l.text).join("\n") + "\n", range: { start: f.md[0].n, end: f.md[f.md.length - 1].n } };
      f.nodes.push(node);
      this.prose.add(node);
    }
    f.md = [];
  }

  private push(f: Frame, node: Node) {
    this.flush(f);
    f.nodes.push(node);
    f.lastRule = node.type === "directive" && RULES.has(node.kind) ? node : null;
    f.prevBlank = f.paragraph = f.prevQuote = f.inList = false;
  }

  // ---- keyword rules, reasons and alerts (§6.1) ----

  private startUnit(f: Frame, line: Line): boolean {
    const t = line.text;
    const alert = ALERT.exec(t);
    if (alert && !f.prevQuote) {
      const keyword = alert[1].toUpperCase().replace(/[ \t]+/g, " ");
      const kind = KEYWORDS[keyword];
      if (kind) {
        const node: DirectiveNode = { type: "directive", kind, form: "alert", attrs: { ...(KEYWORD_FLAGS[keyword] as Attrs | undefined) }, range: { start: line.n, end: line.n } };
        this.keywords.set(node, keyword);
        this.push(f, node);
        f.unit = { type: "alert", node, lines: [], last: t };
        return true;
      }
    }
    // the line's own text, or a list item's, or a quoted line's
    let shape: Shape = "line";
    let content = t;
    let offset = 0;
    const quote = QUOTE.exec(t);
    const item = ITEM.exec(t);
    if (quote) { shape = "quote"; content = quote[1]; }
    else if (item && !THEMATIC.test(t) && (indentOf(item[1]) <= 3 || f.inList)) {
      shape = "item"; content = item[4];
      offset = indentOf(item[1]) + item[2].length + (item[3].replace(/\t/g, "    ").length > 4 ? 1 : item[3].replace(/\t/g, "    ").length);
    } else if (indentOf(t) > 3) return false;
    else content = t.replace(/^[ \t]+/, "");

    const k = KEYWORD.exec(content);
    if (k) {
      const node = this.ruleNode(k, line);
      if (!node) return false;
      this.push(f, node);
      f.unit = { type: "rule", node, lines: [{ text: k[6], n: line.n }], shape, offset, blanks: [] };
      return true;
    }
    const b = BECAUSE.exec(content);
    if (b && (!!b[1] === !!b[2])) {
      const rule = f.lastRule;
      this.flush(f);
      f.unit = { type: "reason", rule, lines: [{ text: b[3], n: line.n }], original: [line], shape, offset, blanks: [] };
      return true;
    }
    return false;
  }

  private ruleNode(m: RegExpExecArray, line: Line): DirectiveNode | null {
    const [, open, keyword, id, cond, close] = m;
    if (!!open !== !!close) return null; // unmatched emphasis is no rule (§6.1)
    const kind = KEYWORDS[keyword];
    const attrs: Attrs = {};
    if (id !== undefined) {
      if (PATTERNS.id.test(id)) attrs.id = id;
      else this.diag("invalid-attributes", "error", `"${id}" is not a valid rule name: use letters, digits, ".", "_" and "-"`, line.n);
    }
    if (cond !== undefined) {
      if (!RULES.has(kind)) this.diag("invalid-attributes", "error", `${keyword} is advice, not a rule: it takes no (if …) condition`, line.n);
      else if (cond.trim() === "") this.diag("invalid-attributes", "error", "a rule's (if …) condition is not empty", line.n);
      else attrs.if = cond;
    }
    Object.assign(attrs, KEYWORD_FLAGS[keyword] as Attrs | undefined);
    const node: DirectiveNode = { type: "directive", kind, form: "keyword", attrs, range: { start: line.n, end: line.n } };
    this.keywords.set(node, keyword);
    if (attrs.id) this.identity(String(attrs.id), kind, line.n);
    return node;
  }

  /** Does `line` carry on the open rule, reason or alert? */
  private continueUnit(f: Frame, line: Line): boolean {
    const u = f.unit!;
    const t = line.text;
    if (u.type === "alert") {
      const q = QUOTE.exec(t);
      if (q) { u.lines.push({ text: q[1], n: line.n }); u.last = q[1]; return true; }
      if (!blank(t) && !blank(u.last) && !startsBlock(t)) { u.lines.push({ text: t.trim(), n: line.n }); u.last = t; return true; }
      return false;
    }
    if (u.shape === "quote") {
      const q = QUOTE.exec(t);
      if (q) {
        if (blank(q[1]) || KEYWORD.test(q[1]) || BECAUSE.test(q[1])) return false;
        if (u.type === "reason") u.original.push(line);
        u.lines.push({ text: q[1], n: line.n });
        return true;
      }
      if (blank(t) || startsBlock(t)) return false;
      if (u.type === "reason") u.original.push(line);
      u.lines.push({ text: t.trim(), n: line.n });
      return true;
    }
    if (u.shape === "item") {
      if (blank(t)) { u.blanks.push(line); return true; }
      if (indentOf(t) >= u.offset) {
        const inner = dedent(t, u.offset);
        if (u.type === "rule" && !u.blanks.length && BECAUSE.test(inner)) {
          // a reason written under the item's text belongs to its rule
          const b = BECAUSE.exec(inner)!;
          const rule = u.node;
          this.endUnit(f);
          f.unit = { type: "reason", rule, lines: [{ text: b[3], n: line.n }], original: [line], shape: "item", offset: u.offset, blanks: [] };
          return true;
        }
        if (u.type === "reason") u.original.push(...u.blanks, line);
        u.lines.push(...u.blanks, { text: inner, n: line.n });
        u.blanks = [];
        return true;
      }
      if (u.blanks.length || startsBlock(t) || interrupts(t)) return false;
      if (u.type === "reason") u.original.push(line);
      u.lines.push({ text: t.trim(), n: line.n });
      return true;
    }
    // a line: its paragraph, until a blank line or a line that starts something else
    if (blank(t) || startsBlock(t) || interrupts(t)) return false;
    if (u.type === "reason") u.original.push(line);
    u.lines.push({ text: t, n: line.n });
    return true;
  }

  private endUnit(f: Frame) {
    const u = f.unit!;
    f.unit = null;
    if (u.type === "alert") {
      u.node.children = this.scope(u.lines, { directives: false, keywords: false, document: false });
      u.node.range!.end = u.lines.length ? u.lines[u.lines.length - 1].n : u.node.range!.start;
      f.lastRule = RULES.has(u.node.kind) ? u.node : null;
      return;
    }
    const pending = u.blanks;
    if (u.type === "rule") {
      u.node.children = u.shape === "item"
        ? this.scope(u.lines, { directives: false, keywords: true, document: false })
        : [this.markdown(u.lines)];
      u.node.range!.end = u.lines[u.lines.length - 1].n;
      f.lastRule = u.node;
    } else {
      const reason = u.lines.map((l) => l.text).join("\n");
      if (!u.rule) {
        this.diag("orphan-reason", "warning", "BECAUSE: follows no rule here, so it stays prose: put it right after the rule it explains", u.lines[0].n);
        for (const l of u.original) this.proseLine(f, l);
      } else if (u.rule.attrs.because !== undefined) {
        this.diag("reason-repeated", "error", "this rule already has a reason: give it one BECAUSE", u.lines[0].n);
        for (const l of u.original) this.proseLine(f, l);
      } else {
        u.rule.attrs.because = reason;
        this.reasons.set(u.rule, { start: u.lines[0].n, end: u.lines[u.lines.length - 1].n });
        f.lastRule = u.rule;
      }
    }
    for (const l of pending) this.proseLine(f, l);
  }

  private markdown(lines: Line[]): MarkdownNode {
    const node: MarkdownNode = { type: "markdown", source: lines.map((l) => l.text).join("\n") + "\n", range: { start: lines[0].n, end: lines[lines.length - 1].n } };
    this.prose.add(node);
    return node;
  }

  // ---- directives (§5) ----

  private open(stack: Frame[], m: RegExpExecArray, line: Line): boolean {
    const colons = m[1].length;
    const kind = m[2];
    const rest = m[3].replace(/[ \t]+$/, "");
    const top = stack[stack.length - 1];
    let attrText: string | null = null;
    let attrBroken = false;
    if (rest) {
      if (rest[0] !== "{") return false; // `:::note words` is prose
      const end = attributesEnd(rest, 0);
      if (end === rest.length) attrText = rest.slice(1, -1);
      else attrBroken = true;
    }
    const leaf = colons === 2;
    const spec = KINDS[kind];
    const extension = kind.includes(":");
    const node: DirectiveNode = { type: "directive", kind, form: leaf ? "leaf" : "container", attrs: {}, range: { start: line.n, end: line.n } };
    const where = `${leaf ? "::" : ":::"}${kind}`;
    if (attrBroken) this.diag("invalid-attributes", "error", `${where}: its attribute list must close with } at the end of the line`, line.n);
    if (attrText !== null) {
      const parsed = parseAttributes(attrText, (key) => (spec ? attrSpec(kind, key) : undefined), !!spec);
      node.attrs = parsed.attrs;
      for (const p of parsed.problems) this.diag("invalid-attributes", "error", `${where}: ${p}`, line.n);
    }
    if (!spec && !extension) this.diag("unknown-kind", "error", `${where} is not a kind of this specification; it is kept as written, uninterpreted`, line.n);
    if (extension) {
      const alias = kind.split(":")[0];
      const ext = (this.front.extensions as Record<string, unknown> | undefined)?.[alias];
      if (!ext) this.diag("extension-unavailable", "error", `${where}: the extension alias "${alias}" is not declared in the front matter's extensions`, line.n);
    }
    if (spec) this.checkDirective(node, spec, stack, line.n, where);
    if (node.attrs.id !== undefined) this.identity(String(node.attrs.id), kind, line.n);
    if (kind === "include" && typeof node.attrs.as === "string") {
      if (this.aliases.has(node.attrs.as)) this.diag("duplicate-id", "error", `the include alias "${node.attrs.as}" is used twice`, line.n);
      this.aliases.add(node.attrs.as);
    }
    if (top.unit) this.endUnit(top);
    if (leaf) {
      this.push(top, node);
      if (kind === "param") this.paramLeaf(node, line.n);
      return true;
    }
    if (stack.length > 1 && colons >= top.fence) {
      this.diag("invalid-nesting", "error", `${where} opens inside a ${":".repeat(top.fence)} container with as many colons or more: an inner fence is shorter than its outer one`, line.n);
    }
    let body: Body = "parsed";
    let format: BodyFormat = "markdown";
    if (!spec) body = "opaque";
    else {
      // a format the kind does not take is reported (checkDirective) and leaves the default body
      const fallback: BodyFormat = spec.body === "yaml" ? "yaml" : "markdown";
      const chosen = node.attrs.format as BodyFormat | undefined;
      format = chosen && (spec.formats ?? [fallback]).includes(chosen) ? chosen : fallback;
      if (format === "markdown") body = spec.opaque || node.attrs.verbatim === true ? "literal" : "parsed";
      else body = "structured";
    }
    this.push(top, node);
    stack.push(this.frame(node, colons, body, format));
    return true;
  }

  private checkDirective(node: DirectiveNode, spec: (typeof KINDS)[string], stack: Frame[], line: number, where: string) {
    if (!spec.forms.includes(node.form)) {
      const forms = spec.forms.filter((x) => x === "container" || x === "leaf");
      this.diag("invalid-nesting", "error", `${where}: ${node.kind} is written ${forms.map((x) => (x === "leaf" ? `::${node.kind}{…}` : `:::${node.kind} … :::`)).join(" or ")}`, line);
    }
    for (const key of spec.required) {
      if (node.attrs[key] === undefined || node.attrs[key] === "") this.diag("invalid-attributes", "error", `${where} needs ${key}=…`, line);
    }
    if (spec.parent) {
      const parent = [...stack].reverse().find((f) => f.node)?.node;
      if (parent?.kind !== spec.parent) this.diag("invalid-nesting", "error", `${where} belongs inside :::${spec.parent}`, line);
    }
    const a = node.attrs;
    if (typeof a.format === "string") {
      const allowed = spec.formats ?? [spec.body === "yaml" ? "yaml" : "markdown"];
      if (!allowed.includes(a.format as BodyFormat)) this.diag("invalid-attributes", "error", `${where}: format is ${allowed.join(" or ")}`, line);
    }
    if (a.mode === "pinned" && a.sha256 === undefined) this.diag("pin-mismatch", "error", `${where}: a pinned reference needs sha256=sha256:<64 hex>`, line);
    // an A2A agent is known by its Agent Card, its endpoint or both (§10)
    if (node.kind === "agent" && a.protocol === "a2a" && !a.src && !a.endpoint) this.diag("invalid-attributes", "error", `${where}: an A2A agent needs src=… (its Agent Card) or endpoint=… (where it receives messages)`, line);
    if (typeof a.start === "number" && typeof a.end === "number" && a.end <= a.start) this.diag("invalid-attributes", "error", `${where}: end is after start`, line);
    if (typeof a.region === "string" && !validRegion(a.region)) this.diag("invalid-attributes", "error", `${where}: region is x,y,width,height, fractions in [0,1] inside the unit square`, line);
  }

  private close(stack: Frame[], colons: number, n: number, atEof = false) {
    const f = stack.pop()!;
    if (!atEof && colons < f.fence) this.diag("invalid-nesting", "error", `the closing fence on line ${n} is shorter than the :::${f.node!.kind} it closes (${f.fence} colons)`, n);
    f.node!.range!.end = n;
    this.endFrame(f);
    const parent = stack[stack.length - 1];
    parent.lastRule = RULES.has(f.node!.kind) ? f.node : null;
    parent.prevBlank = parent.paragraph = parent.prevQuote = parent.inList = false;
  }

  private endFrame(f: Frame) {
    const node = f.node;
    if (f.body === "parsed") {
      if (f.unit) this.endUnit(f);
      this.flush(f);
      if (node) node.children = f.nodes;
      return;
    }
    const text = f.raw.length ? f.raw.map((l) => l.text).join("\n") + "\n" : "";
    const at = f.raw[0]?.n ?? node!.range!.start;
    if (f.body === "literal" || f.body === "opaque") {
      node!.children = text ? [{ type: "markdown", source: text, range: { start: at, end: f.raw[f.raw.length - 1].n } }] : [];
      if (f.body === "opaque") node!.source = this.lines.slice(node!.range!.start - 1, node!.range!.end).join("\n") + "\n";
      return;
    }
    const value: StructuredValue = { format: f.format as StructuredValue["format"], source: text };
    if (f.format === "yaml" || f.format === "json") {
      const r = f.format === "yaml" ? loadYaml(text) : loadJson(text);
      if (!r.ok) this.diag("invalid-structured-body", "error", `:::${node!.kind}: its ${f.format.toUpperCase()} body is invalid: ${r.message}`, at);
      else {
        value.data = r.value;
        // data may be any JSON value; every other structured kind is a mapping
        if (node!.kind !== "data" && (r.value === null || typeof r.value !== "object" || Array.isArray(r.value))) this.diag("invalid-structured-body", "error", `:::${node!.kind}: its body is a ${f.format.toUpperCase()} mapping`, at);
      }
    } else if (f.format === "csv") {
      const r = loadCsv(text);
      if (!r.ok) this.diag("invalid-structured-body", "error", `:::${node!.kind}: ${r.message}`, at);
      else value.data = r.rows.map((row) => Object.fromEntries(r.header.map((h, i) => [h, row[i]])));
    }
    node!.value = value;
  }

  /** A line inside a literal, structured or uninterpreted body: only its closing fence is read. */
  private rawLine(stack: Frame[], line: Line) {
    const f = stack[stack.length - 1];
    if (f.rawCode) {
      f.raw.push(line);
      if (closesFence(line.text, f.rawCode)) f.rawCode = null;
      return;
    }
    const close = CLOSE.exec(line.text);
    if (close && close[1].length >= f.fence) { this.close(stack, close[1].length, line.n); return; }
    if (f.body === "literal") {
      const fence = FENCE.exec(line.text);
      if (fence && !(fence[2][0] === "`" && fence[3].includes("`"))) f.rawCode = { char: fence[2][0], len: fence[2].length };
    }
    f.raw.push(line);
  }

  // ---- metadata ----

  private backmatter(lines: Line[], li: number) {
    const rest = lines.slice(li + 1);
    this.backRange = { start: lines[li].n, end: rest.length ? rest[rest.length - 1].n : lines[li].n };
    this.back = this.metadata(rest.map((l) => l.text).join("\n"), lines[li].n + 1, "backmatter");
    const sources = this.back.sources;
    if (sources !== undefined && !Array.isArray(sources)) this.diag("invalid-metadata", "error", "the backmatter's sources are a list of {id, title, uri} records", lines[li].n);
  }

  private checkFront() {
    const f = this.front;
    const at = this.frontRange?.start ?? 1;
    if ("spec" in f) {
      if (f.spec === 1) f.spec = "1";
      if (f.spec !== "1") this.diag("unsupported-format", "error", `spec: "${String(f.spec)}" is not a format this reader knows; it reads spec: "1"`, at);
    }
    for (const key of ["id", "version", "title", "description", "language", "license"]) {
      if (key in f && typeof f[key] !== "string") this.diag("invalid-metadata", "error", `the front matter's ${key} is a string`, at);
    }
    if ("tags" in f && !(Array.isArray(f.tags) && f.tags.every((t) => typeof t === "string"))) this.diag("invalid-metadata", "error", "the front matter's tags are a list of strings", at);
    for (const key of ["parameters", "extensions", "references", "resolvers", "exports", "presentation"]) {
      if (key in f && (f[key] === null || typeof f[key] !== "object" || Array.isArray(f[key]))) this.diag("invalid-metadata", "error", `the front matter's ${key} is a mapping`, at);
    }
    if ("extends" in f) {
      if (!Array.isArray(f.extends)) this.diag("invalid-metadata", "error", "extends is a list of {uri, as} records", at);
      else for (const base of f.extends as unknown[]) {
        const b = base as Record<string, unknown> | null;
        if (!b || typeof b !== "object" || typeof b.uri !== "string" || typeof b.as !== "string") this.diag("invalid-metadata", "error", "each extends record has a uri and an as alias", at);
        else if (this.aliases.has(b.as)) this.diag("duplicate-id", "error", `the base alias "${b.as}" is used twice`, at);
        else this.aliases.add(b.as);
      }
    }
    // (a mapping, as checked above: anything else binds nothing)
    const extensions = (f.extensions && typeof f.extensions === "object" && !Array.isArray(f.extensions) ? f.extensions : {}) as Record<string, unknown>;
    for (const [alias, binding] of Object.entries(extensions)) {
      const b = binding as Record<string, unknown> | null;
      if (!b || typeof b !== "object" || typeof b.id !== "string" || typeof b.version !== "string") this.diag("invalid-metadata", "error", `the extension "${alias}" binds an exact id and version`, at);
    }
    if ("requires" in f) {
      if (!Array.isArray(f.requires)) this.diag("invalid-metadata", "error", "requires is a list of extension aliases", at);
      else for (const alias of f.requires as unknown[]) if (typeof alias !== "string" || !Object.hasOwn(extensions, alias)) this.diag("extension-unavailable", "error", `requires names "${String(alias)}", which the extensions do not declare`, at);
    }
    const params = f.parameters;
    if (params && typeof params === "object" && !Array.isArray(params)) {
      for (const [name, decl] of Object.entries(params as Record<string, unknown>)) {
        if (decl === null || typeof decl !== "object" || Array.isArray(decl)) { this.diag("parameter-invalid", "error", `the parameter "${name}" is declared with a mapping`, at); continue; }
        this.declare(name, decl as Record<string, unknown>, "front-matter", at);
      }
    }
  }

  private paramLeaf(node: DirectiveNode, line: number) {
    const a = node.attrs;
    if (typeof a.name !== "string") return;
    this.declare(a.name, a as Record<string, unknown>, line, line);
  }

  private declare(name: string, d: Record<string, unknown>, at: Parameter["declaredAt"], line: number) {
    if (!PATTERNS.parameter.test(name)) { this.diag("parameter-invalid", "error", `"${name}" is not a valid parameter name`, line); return; }
    if (this.parameters.has(name)) { this.diag("parameter-invalid", "error", `the parameter "${name}" is declared twice`, line); return; }
    const type = typeof d.type === "string" ? d.type : "string";
    const values = Array.isArray(d.values) ? d.values.map(String) : typeof d.values === "string" ? d.values.split(",").map((s) => s.trim()) : undefined;
    const p: Parameter = {
      name, type, required: d.required === true, values, declaredAt: at,
      ...(d.default !== undefined ? { default: d.default } : {}),
      ...(d.example !== undefined ? { example: d.example } : {}),
      ...(typeof d.description === "string" ? { description: d.description } : {}),
      ...(typeof d.source === "string" ? { source: d.source } : {}),
    };
    this.parameters.set(name, p);
  }

  private checkParameters() {
    for (const p of this.parameters.values()) {
      const line = p.declaredAt === "front-matter" ? this.frontRange?.start ?? 1 : p.declaredAt;
      if (!["string", "number", "integer", "boolean", "enum", "list", "url", "duration"].includes(p.type)) { this.diag("parameter-invalid", "error", `the parameter "${p.name}" has an unknown type "${p.type}"`, line); continue; }
      if (p.type === "enum" && !(p.values && p.values.length && p.values.every((v) => v !== ""))) this.diag("parameter-invalid", "error", `the enum parameter "${p.name}" lists its values`, line);
      if (p.default !== undefined && !valueFits(p, p.default)) this.diag("parameter-invalid", "error", `the default of "${p.name}" is not a valid ${p.type}`, line);
    }
  }

  // ---- identities ----

  private identity(id: string, kind: string, line: number) {
    const seen = this.identities.get(id);
    if (seen) { this.diag("duplicate-id", "error", `the id "${id}" is used twice (first on line ${seen.line}); ids are unique in a document`, line); return; }
    this.identities.set(id, { id, kind, line });
  }

  private headingIdentity(list: string, line: number) {
    const parsed = parseAttributes(list.slice(1, -1), (key) => (key === "id" || key === "class" || key === "style" ? attrSpec("section", key) : undefined));
    for (const p of parsed.problems) this.diag("invalid-attributes", "error", `heading attributes: ${p}; a heading takes only id, class and style`, line);
    if (typeof parsed.attrs.id === "string") this.identity(parsed.attrs.id, "heading", line);
  }
}

// ---- helpers ----

function closesFence(text: string, f: Fence): boolean {
  const m = /^(?:[ \t]*>)*[ \t]*(`+|~+)[ \t]*$/.exec(text);
  return !!m && m[1][0] === f.char && m[1].length >= f.len;
}

/** Does this line start a block that ends a paragraph (CommonMark's interruptions, and a directive)? */
function startsBlock(t: string): boolean {
  return HEADING.test(t) || THEMATIC.test(t) || /^ {0,3}>/.test(t) || ITEM_START.test(t) || FENCE.test(t) || MATH.test(t) || /^:{2,}[a-z]/.test(t) || CLOSE.test(t) || HTML_BLOCK.test(t) || HTML_LITERAL.some(([o]) => o.test(t));
}

/** A keyword or reason line, which interrupts the rule before it (§6.1). */
function interrupts(t: string): boolean {
  const s = t.replace(/^ {0,3}/, "");
  return KEYWORD.test(s) || BECAUSE.test(s);
}

function dedent(t: string, n: number): string {
  let col = 0, i = 0;
  while (i < t.length && col < n && (t[i] === " " || t[i] === "\t")) { col += t[i] === "\t" ? 4 - (col % 4) : 1; i++; }
  return t.slice(i);
}

/** §3.2 rule 2: the first nonblank, non-comment line after the separator begins with a known key. */
function backmatterFollows(lines: Line[], li: number): boolean {
  for (let j = li + 1; j < lines.length; j++) {
    const t = lines[j].text;
    if (blank(t) || /^[ \t]*#/.test(t)) continue;
    return new RegExp(`^(?:${BACKMATTER_KEYS.join("|")}):(?:[ \\t]|$)`).test(t);
  }
  return false;
}

/** The line that closes a `$$` region opened at `li` — before any closing fence: a formula cannot cross a directive boundary. */
function mathEnd(lines: Line[], li: number): number {
  for (let j = li + 1; j < lines.length; j++) {
    const t = lines[j].text;
    if (MATH.test(t)) return lines[j].n;
    if (CLOSE.test(t)) return 0;
  }
  return 0;
}

/**
 * Remove comment bytes from one line (§3.3). `open`: a comment from an earlier line is still open.
 * `text` is null when nothing but the comment (and spaces) was on the line. Comment-looking text in
 * a backtick code span stays.
 */
export function stripComments(line: string, open: boolean): { text: string | null; open: boolean; opened: boolean } {
  let out = "";
  let i = 0;
  let removed = false;
  let opened = false;
  while (i < line.length) {
    if (open) {
      const end = line.indexOf("-->", i);
      if (end < 0) { removed = true; i = line.length; break; }
      open = false; removed = true; i = end + 3; continue;
    }
    const c = line[i];
    if (c === "\\") { out += line.slice(i, i + 2); i += 2; continue; }
    if (c === "`") {
      const run = /^`+/.exec(line.slice(i))![0];
      const close = line.indexOf(run, i + run.length);
      // a code span closes with a run of exactly the same length
      if (close >= 0 && line[close + run.length] !== "`") { out += line.slice(i, close + run.length); i = close + run.length; continue; }
      out += run; i += run.length; continue;
    }
    if (line.startsWith("<!--", i)) { open = true; opened = true; removed = true; i += 4; continue; }
    out += c; i++;
  }
  if (removed && out.trim() === "") return { text: null, open, opened };
  return { text: out, open, opened };
}

function validRegion(s: string): boolean {
  const parts = s.split(",").map((p) => p.trim());
  if (parts.length !== 4 || parts.some((p) => !/^(?:0|1|0?\.[0-9]+|1\.0+)$/.test(p))) return false;
  const [x, y, w, h] = parts.map(Number);
  return w > 0 && h > 0 && x + w <= 1 && y + h <= 1;
}

/** A value written as text (a command line's `name=value`, an element's attribute), as its declared type reads it: a
 *  number, `true` or `false`, a comma-separated list; the text itself for the other types, and where it does not read
 *  as its type (valueFits then refuses it). */
export function valueFrom(p: Pick<Parameter, "type">, text: string): unknown {
  switch (p.type) {
    case "number": case "integer": { const n = Number(text); return text.trim() !== "" && Number.isFinite(n) ? n : text; }
    case "boolean": return text === "true" ? true : text === "false" ? false : text;
    case "list": return text.trim() === "" ? [] : text.split(",").map((x) => x.trim());
    default: return text;
  }
}

/** Does a value satisfy a parameter's declared type (§12)? */
export function valueFits(p: Pick<Parameter, "type" | "values">, v: unknown): boolean {
  switch (p.type) {
    case "string": return typeof v === "string";
    case "number": return typeof v === "number" && Number.isFinite(v);
    case "integer": return typeof v === "number" && Number.isInteger(v);
    case "boolean": return typeof v === "boolean";
    case "enum": return typeof v === "string" && !!p.values?.includes(v);
    case "list": return Array.isArray(v) && v.every((x) => typeof x === "string");
    case "url": return typeof v === "string" && /^https?:\/\/[^\s]+$/i.test(v);
    case "duration": return typeof v === "string" && /^[0-9]+(?:ms|s|m|h|d|w)$/.test(v);
    default: return false;
  }
}
